import { Component, Input } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { Subject, takeUntil } from 'rxjs';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { CommonModule } from '@angular/common';
import { PopupTableComponent } from '../../global-components/popup-table/popup-table.component';
import { MatDialog } from '@angular/material/dialog';
import { GlobalService } from '../../shared-services/global.service';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-tree-chart',
  imports: [DataErrorComponent, CommonModule],
  templateUrl: './tree-chart.component.html',
  styleUrl: './tree-chart.component.scss',
})
export class TreeChartComponent {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  private unSubscribe$: Subject<void> = new Subject();
  public hasError = false;
  public errorMessage = '';
  private chartData: any = null;
  filters: any;

  ngAfterViewInit() {
    const filters = this.service.getAllFilters();
    this.filters = filters.filter;
    this.loadAndRenderChart(filters.filter);
  }
  constructor(
    private configService: ConfigService,
    private service: GlobalService,
    private dialog: MatDialog,
    private router: Router
  ) { }
  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }
    this.myChart.showLoading();
    if (this.data.setExtrakey) {
      this.data.extraKeys = [
        ...((this.data.extraKeys ?? []) as any[]),
        { version: filters.version[0] },
      ];
    }

    this.configService
      .fetchData(this.data.apiUrl, this.data.functionName, filters, this.data)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          this.chartData = this.data?.responseKey
            ? res[0][this.data.responseKey]
            : res.responseList[0]
              ? res.responseList[0].responseData[0]
              : res;
          const isEmpty = !this.chartData;

          if (isEmpty) {
            this.hasError = true;
            this.errorMessage = 'No data found for the selected filters.';
            this.myChart.clear();
          } else {
            this.hasError = false;
            this.renderChart();
          }

          this.myChart.hideLoading();
          window.addEventListener('resize', this.resizeChart);
        },
        error: (err) => {
          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
          this.myChart?.clear();
          this.myChart.hideLoading();
        },
      });
  }

  private mapToEcharts(
    node: any,
    depth: number = 0,
    index: number = 0,
    parent: any = null
  ): any {
    const colors = ['#4d4d4d', '#bfbfbf', '#6bbf59', '#007acc', '#f5a623'];

    const projectCount = node.projects ?? 0;
    const amount = node.amount ? ` (${node.amount})` : '';

    return {
      name: `${node.title || ''}:\n${projectCount} Project${projectCount === 1 ? '' : 's'
        }${amount}`,
      itemStyle: { color: colors[depth % colors.length] },
      label: { color: '#fff' },
      depth,
      node: node,
      parentNode: parent,
      children: node.children?.map((child: any, i: number) =>
        this.mapToEcharts(child, depth + 1, i, node)
      ),
    };
  }

  private renderChart(): void {
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
      },
      series: [
        {
          type: 'tree',
          data: [this.mapToEcharts(this.chartData)],
          top: '5%',
          left: '15%',
          bottom: '5%',
          right: '30%',
          symbol: 'rect',
          symbolSize: [250, 50],
          label: {
            position: 'inside',
            verticalAlign: 'middle',
            align: 'center',
            fontSize: 14,
            lineHeight: 16,
          },
          lineStyle: {
            color: '#999',
            width: 3,
          },
          expandAndCollapse: false,
          edgeShape: 'polyline',
          edgeForkPosition: '50%',
          initialTreeDepth: -1,
          animationDuration: 500,
          animationDurationUpdate: 750,
        },
      ],
    };

    this.myChart.setOption(option);
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      const nodeDepth = params.data?.depth;
      let filter;
      if (nodeDepth == 0) {
        filter = {
          ...this.filters,
        };
      } else if (nodeDepth == 1) {
        filter = {
          ...this.filters,
          cost_interval: params.data.node.filter,
        };
      } else if (nodeDepth == 2) {
        filter = {
          ...this.filters,
          cost_interval: params.data.parentNode.filter,
          final_phi_parameter: [params.data.node.title],
        };
      }
      const tableFilter = { ...filter };
      if ('version' in tableFilter) {
        delete tableFilter.version;
      }

      this.dialog.open(PopupTableComponent, {
        width: '1200px',
        height: '700px',
        maxWidth: '100vw',
        maxHeight: '100vh',
        data: {
          headers: [
            { key: 'title', label: 'Title', width: 600 },
            { key: 'final_em', label: 'Engine Model' },
            { key: 'cleaned_vertical', label: 'Vertical' },
            { key: 'calculated_annualized_savings', label: 'Savings' },
            { key: 'total_cost', label: 'Total Cost' },
          ],
          filter: tableFilter,
          apiUrl: this.data.tabelUrl || this.data.apiUrl,
          functionName: this.data.tableFunction,
        },
      });
    });
  }

  private resizeChart = () => {
    this.myChart?.resize();
  };

  onClose() {
    this.router.navigate([this.data.url]);
  }
}

